'use client';
import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import Badge from '@/components/ui/Badge';
import { Eye, UserCheck, Calendar, Clock, CheckCircle, RefreshCw, X, Plus, PoundSterling } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

type ComplaintStatus = 'open' | 'under_review' | 'resolved' | 'reprocess';

interface Complaint {
  id: string;
  jobId: string;
  customer: string;
  cleaner: string;
  service: string;
  date: string;
  issueType: string;
  description: string;
  status: ComplaintStatus;
  photos: number;
  photoUrls: string[];
  createdAt: string;
  priority: 'high' | 'medium' | 'low';
}

interface ComplaintDetailModalProps {
  complaint: Complaint | null;
  onClose: () => void;
  onStatusUpdate: (id: string, status: ComplaintStatus) => void;
  onReprocessCreated: (complaintId: string) => void;
}

const availableCleaners = [
  'James Okafor',
  'Marcus Johnson',
  'Devon Richards',
  'Maria Gonzalez',
  'Sarah Thompson',
  'David Patel',
  'Emma Wilson',
  'Chris Brown',
];

const statusVariant = (s: ComplaintStatus) => {
  if (s === 'open') return 'danger';
  if (s === 'under_review') return 'warning';
  if (s === 'reprocess') return 'info';
  return 'success';
};

const statusLabel = (s: ComplaintStatus) => {
  if (s === 'open') return 'Open';
  if (s === 'under_review') return 'Under Review';
  if (s === 'reprocess') return 'Reprocess';
  return 'Resolved';
};

const priorityColor: Record<string, string> = {
  high: 'text-danger',
  medium: 'text-warning',
  low: 'text-muted-foreground',
};

export default function ComplaintDetailModal({
  complaint,
  onClose,
  onStatusUpdate,
  onReprocessCreated,
}: ComplaintDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'reprocess'>('details');
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [selectedCleaners, setSelectedCleaners] = useState<string[]>([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [reprocessNotes, setReprocessNotes] = useState('');
  const [reprocessPrice, setReprocessPrice] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [cleanerSearch, setCleanerSearch] = useState('');

  const toggleCleaner = (name: string) => {
    setSelectedCleaners(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const filteredCleaners = availableCleaners.filter(c =>
    c.toLowerCase().includes(cleanerSearch.toLowerCase())
  );

  const handleReprocess = async () => {
    if (!complaint) return;
    if (selectedCleaners.length === 0) {
      toast.error('Please assign at least one cleaner');
      return;
    }
    if (!scheduledDate) {
      toast.error('Please select a date');
      return;
    }
    if (!scheduledTime) {
      toast.error('Please select a time');
      return;
    }

    setSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('reprocessed_jobs').insert({
        original_job_id: complaint.jobId,
        complaint_id: complaint.id,
        customer_name: complaint.customer,
        service: complaint.service,
        assigned_cleaners: selectedCleaners,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
        notes: reprocessNotes || null,
        price: reprocessPrice !== '' ? parseFloat(reprocessPrice) : 0,
        status: 'reprocess',
      });

      if (error) throw error;

      onStatusUpdate(complaint.id, 'reprocess');
      onReprocessCreated(complaint.id);
      toast.success('Reprocessed job created successfully');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create reprocessed job';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!complaint) return null;

  return (
    <>
      <Modal open={!!complaint} onClose={onClose} title="Complaint Details" size="lg">
        {/* Tab navigation */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-4">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'details' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('reprocess')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === 'reprocess' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <RefreshCw size={13} />
            Assign & Reprocess
          </button>
        </div>

        {activeTab === 'details' && (
          <div className="space-y-4">
            {/* Status + Priority header */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={statusVariant(complaint.status)} label={statusLabel(complaint.status)} size="sm" />
              <span className={`text-xs font-bold uppercase ${priorityColor[complaint.priority]}`}>
                {complaint.priority} priority
              </span>
              <span className="text-xs text-muted-foreground ml-auto">{complaint.createdAt}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Customer</p>
                <p className="font-semibold text-foreground">{complaint.customer}</p>
                <p className="text-xs text-muted-foreground">{complaint.jobId}</p>
              </div>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Assigned Cleaner</p>
                <p className="font-semibold text-foreground">{complaint.cleaner}</p>
              </div>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Service</p>
                <p className="font-semibold text-foreground">{complaint.service}</p>
              </div>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Service Date</p>
                <p className="font-semibold text-foreground">{complaint.date}</p>
              </div>
              <div className="bg-muted rounded-xl p-3 col-span-2">
                <p className="text-xs text-muted-foreground mb-0.5">Issue Type</p>
                <p className="font-semibold text-foreground">{complaint.issueType}</p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Description</p>
              <p className="text-sm text-foreground leading-relaxed bg-muted rounded-xl p-3">
                {complaint.description}
              </p>
            </div>

            {complaint.photos > 0 && (
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Eye size={14} />
                  <span className="font-semibold">
                    {complaint.photos} issue photo{complaint.photos > 1 ? 's' : ''} attached
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {complaint.photoUrls.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setLightboxPhoto(url)}
                      className="relative aspect-video rounded-xl overflow-hidden border border-border hover:border-primary/50 hover:scale-[1.02] transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
                      aria-label={`View issue photo ${idx + 1}`}
                    >
                      <img src={url} alt={`Issue photo ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Status actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {complaint.status !== 'under_review' && complaint.status !== 'resolved' && complaint.status !== 'reprocess' && (
                <button
                  onClick={() => onStatusUpdate(complaint.id, 'under_review')}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-warning/30 bg-warning-bg text-warning text-sm font-semibold hover:bg-warning/10 transition-colors active:scale-95 whitespace-nowrap"
                >
                  <Clock size={14} className="shrink-0" /><span>Mark Under Review</span>
                </button>
              )}
              {complaint.status !== 'resolved' && complaint.status !== 'reprocess' && (
                <button
                  onClick={() => onStatusUpdate(complaint.id, 'resolved')}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-success-bg border border-success/30 text-success text-sm font-semibold hover:bg-success/10 transition-colors active:scale-95 whitespace-nowrap"
                >
                  <CheckCircle size={14} className="shrink-0" /><span>Resolve Complaint</span>
                </button>
              )}
              <button
                onClick={() => setActiveTab('reprocess')}
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-info-bg border border-info/30 text-info text-sm font-semibold hover:bg-info/10 transition-colors active:scale-95 whitespace-nowrap"
              >
                <RefreshCw size={14} className="shrink-0" /><span>Reprocess Job</span>
              </button>
            </div>
          </div>
        )}

        {activeTab === 'reprocess' && (
          <div className="space-y-5">
            {/* Info banner */}
            <div className="bg-info-bg border border-info/20 rounded-xl p-3 flex items-start gap-2">
              <RefreshCw size={15} className="text-info flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-info">Reprocess Job</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Assign cleaner(s) to redo the work for <strong>{complaint.customer}</strong> ({complaint.jobId}). 
                  This will appear as a reprocessed job on the customer&apos;s account and on the assigned cleaner&apos;s dashboard.
                </p>
              </div>
            </div>

            {/* Cleaner selection */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <UserCheck size={14} className="text-primary" />
                Assign Cleaner(s)
              </p>
              <input
                type="text"
                placeholder="Search cleaners..."
                value={cleanerSearch}
                onChange={e => setCleanerSearch(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-muted text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 mb-2"
              />
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {filteredCleaners.map(cleaner => {
                  const selected = selectedCleaners.includes(cleaner);
                  return (
                    <button
                      key={cleaner}
                      onClick={() => toggleCleaner(cleaner)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                        selected
                          ? 'bg-primary/10 border-primary/40 text-primary' :'bg-muted border-border text-foreground hover:border-primary/30'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selected && <CheckCircle size={10} className="text-white" />}
                      </div>
                      {cleaner}
                    </button>
                  );
                })}
              </div>
              {selectedCleaners.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedCleaners.map(c => (
                    <span
                      key={c}
                      className="flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-full"
                    >
                      {c}
                      <button onClick={() => toggleCleaner(c)} className="hover:text-danger transition-colors">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                  <Calendar size={13} className="text-primary" />
                  Date
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                  <Clock size={13} className="text-primary" />
                  Time
                </label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="text-sm font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                <PoundSterling size={13} className="text-primary" />
                Job Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">£</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={reprocessPrice}
                  onChange={e => setReprocessPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Set the price for this reprocessed job (£0 or above)</p>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-semibold text-foreground mb-1.5 block">
                Notes for Cleaner (optional)
              </label>
              <textarea
                value={reprocessNotes}
                onChange={e => setReprocessNotes(e.target.value)}
                placeholder="e.g. Please focus on the oven interior and kitchen surfaces..."
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleReprocess}
              disabled={submitting || selectedCleaners.length === 0 || !scheduledDate || !scheduledTime}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Reprocessed Job...
                </span>
              ) : (
                <>
                  <Plus size={15} />
                  Create Reprocessed Job
                </>
              )}
            </button>
          </div>
        )}
      </Modal>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm flex items-center gap-1"
            >
              ✕ Close
            </button>
            <img
              src={lightboxPhoto}
              alt="Issue photo enlarged"
              className="w-full rounded-2xl shadow-2xl object-contain max-h-[80vh]"
            />
          </div>
        </div>
      )}
    </>
  );
}
